import type { Location } from '@sda/utils/location/interfaces';

const GOOGLE_TILE_SIZE = 256;

const latLng2World = ({ lat, lng }: Location) => {
  const sin = Math.sin((lat * Math.PI) / 180);
  const x = lng / 360 + 0.5;
  let y = 0.5 - (0.25 * Math.log((1 + sin) / (1 - sin))) / Math.PI;

  y = y < 0 // eslint-disable-line
    ? 0
    : y > 1
      ? 1
      : y;

  return { x, y };
};

// Thank you wiki https://en.wikipedia.org/wiki/Geographic_coordinate_system
const latLng2MetersPerDegree = ({ lat }: Pick<Location, 'lat'>) => {
  const phi = (lat * Math.PI) / 180;

  const metersPerLatDegree = 111132.92
    - 559.82 * Math.cos(2 * phi)
    + 1.175 * Math.cos(4 * phi)
    - 0.0023 * Math.cos(6 * phi);

  const metersPerLngDegree = 111412.84 * Math.cos(phi)
    - 93.5 * Math.cos(3 * phi)
    + 0.118 * Math.cos(5 * phi);

  return { metersPerLatDegree, metersPerLngDegree };
};

const meters2LatLngBounds = (meters: number, { lat, lng }: Location) => {
  const { metersPerLatDegree, metersPerLngDegree } = latLng2MetersPerDegree({
    lat,
  });

  const latDelta = (0.5 * meters) / metersPerLatDegree;
  const lngDelta = (0.5 * meters) / metersPerLngDegree;

  return {
    nw: {
      lat: lat - latDelta,
      lng: lng - lngDelta,
    },
    se: {
      lat: lat + latDelta,
      lng: lng + lngDelta,
    },
  };
};

const meters2WorldSize = (meters: number, { lat, lng }: Location) => {
  const { nw, se } = meters2LatLngBounds(meters, { lat, lng });

  const nwWorld = latLng2World(nw);
  const seWorld = latLng2World(se);

  const w = Math.abs(seWorld.x - nwWorld.x);
  const h = Math.abs(seWorld.y - nwWorld.y);

  return { w, h };
};

/**
 * Calculates the height and width of a circle based on its diameter in meters and its location on the globe
 * @param  {number} meters Diameter
 * @param  {Location} location
 * @param  {number} zoom
 * @return
 */
export const meters2ScreenPixels = (meters: number, { lat, lng }: Location, zoom: number) => {
  const { w, h } = meters2WorldSize(meters, { lat, lng });

  const scale = 2 ** zoom;
  const wScreen = w * scale * GOOGLE_TILE_SIZE;
  const hScreen = h * scale * GOOGLE_TILE_SIZE;

  return {
    w: wScreen,
    h: hScreen,
  };
};
